import { ContactModel } from '@fi-app/components/contact.model';
import { ILightWeightObject } from '@fi-shared/models';
import { LightWeightClass } from '@fi-app/core/light-weight-class';
import { AddressModel, IAddressModel } from '@fi-app/utilities/address/address.model';
import { ICreateChangeBase } from '@fi-shared/models/models';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { IContactModel, IDataFieldValueModel } from '@fi-shared/models/common';

export interface IBusinessUnitModel extends ICreateChangeBase {
    Id?: number;
    Name?: string;
    Description?: string;
    IsSystemObject?: boolean;
    ClusterId?: number;
    StartDate?: string; //ISO 8601 Date
    EndDate?: string; //ISO 8601 Date
    IsActivated?: boolean;
    IsBusinessUnitActive?: boolean;
    MediaBankPhysicalFileName?: string;
    MediaBankExtension?: string;
    MediaBankFileReference?: IMediaBankFileReference;
    OriginalMediaBankFileReference?: IMediaBankFileReference;
    Cluster?: ILightWeightObject;
    Area?: ILightWeightObject;
    AreaId?: number;
    Users?: ILightWeightObject[];
    BusinessAreas?: ILightWeightObject[];
    ConnectedUserIds: number[];
    BusinessAreaIds: number[];

    EmailAddressContacts: IContactModel[];
    PhoneNumberContacts: IContactModel[];
    Addresses: IAddressModel[];

    IsExpired: boolean;

    DataFieldValues: IDataFieldValueModel[];
    OrganisationNumber: string;
}

export class BusinessUnitModel {
    Id: number = 0;
    Name: string = '';
    IsActivated: boolean = true;
    IsSystemObject: boolean = false;
    Description = '';
    Website = '';
    ClusterId = undefined;
    Users: ILightWeightObject[] = [];
    BusinessAreas: ILightWeightObject[] = [];
    BusinessAreaIds: number[] = [];
    CreatedByUserId = 0;
    CreatedByUserName = '';
    CreatedOn: string; //ISO 8601 date
    ChangedByUserId = 0;
    ChangedByUserName = '';
    ChangedOn: string; //ISO 8601 date
    Addresses: IAddressModel[] = [];
    EmailAddressContacts: IContactModel[] = [];
    PhoneNumberContacts: IContactModel[] = [];
    MediaBankPhysicalFileName = '';
    MediaBankExtension = '';
    Area: ILightWeightObject = new LightWeightClass();
    AreaId = undefined;
    AreaName = '';
    Cluster: ILightWeightObject = new LightWeightClass();
    ClusterName = '';
    StartDate: Date | string = new Date();
    EndDate: Date | string;
    MediaBankFileReference: IMediaBankFileReference = null;
    OriginalMediaBankFileReference: IMediaBankFileReference = null;
    ConnectedUserIds: number[] = [];
    IsExpired = false;
    DataFieldValues: IDataFieldValueModel[] = [];
    IsBusinessUnitActive = true;
    OrganisationNumber: string;

    constructor() {
        const startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        this.StartDate = startDate;
    }

    Initialize(businessUnit: IBusinessUnitModel) {
        this.Id = businessUnit.Id;
        this.Name = businessUnit.Name;
        this.Description = businessUnit.Description;
        this.ClusterId = businessUnit.ClusterId;
        this.CreatedByUserId = businessUnit.CreatedByUserId;
        this.CreatedByUserName = businessUnit.CreatedByUserName;
        this.CreatedOn = businessUnit.CreatedOn;
        this.ChangedByUserId = businessUnit.ChangedByUserId;
        this.ChangedByUserName = businessUnit.ChangedByUserName;
        this.ChangedOn = businessUnit.ChangedOn;
        this.StartDate = businessUnit.StartDate;
        this.EndDate = businessUnit.EndDate;
        this.IsActivated = businessUnit.IsActivated;
        this.IsBusinessUnitActive = businessUnit.IsBusinessUnitActive;
        this.IsSystemObject = businessUnit.IsSystemObject || false;
        this.Area.Id = businessUnit.Area.Id;
        this.Area.Name = businessUnit.Area.Name;
        this.AreaName = businessUnit.Area.Name;
        this.Area.IsSystemObject = businessUnit.Area.IsSystemObject;
        this.AreaId = businessUnit.AreaId;
        if (businessUnit.Cluster) {
            this.Cluster.Id = businessUnit.Cluster.Id;
            this.Cluster.Name = this.ClusterName = businessUnit.Cluster.Name;
            this.Cluster.IsSystemObject = businessUnit.Cluster.IsSystemObject;
        }
        this.MediaBankPhysicalFileName = businessUnit.MediaBankPhysicalFileName;
        this.MediaBankExtension = businessUnit.MediaBankExtension;
        this.MediaBankFileReference = businessUnit.MediaBankFileReference;
        this.OriginalMediaBankFileReference = businessUnit.OriginalMediaBankFileReference
            ? businessUnit.OriginalMediaBankFileReference
            : businessUnit.MediaBankFileReference;

        businessUnit.Users.forEach((userData) => this.Users.push(new LightWeightClass(userData)));
        businessUnit.BusinessAreas.forEach((businessAreaData) =>
            this.BusinessAreas.push(new LightWeightClass(businessAreaData)),
        );

        this.BusinessAreaIds = businessUnit.BusinessAreaIds;
        this.ConnectedUserIds = businessUnit.ConnectedUserIds;
        this.IsExpired = businessUnit.IsExpired;
        this.OrganisationNumber = businessUnit.OrganisationNumber;
        this.DataFieldValues = businessUnit.DataFieldValues;
    }

    InitializeContacts(response: IBusinessUnitModel) {
        if (response && response.EmailAddressContacts && response.EmailAddressContacts.length > 0) {
            response.EmailAddressContacts.forEach((emailAddressContact) =>
                this.EmailAddressContacts.push(new ContactModel(emailAddressContact)),
            );
        }

        if (response && response.PhoneNumberContacts && response.PhoneNumberContacts.length > 0) {
            response.PhoneNumberContacts.forEach((phoneNumberContact) =>
                this.PhoneNumberContacts.push(new ContactModel(phoneNumberContact)),
            );
        }
    }

    InitializeAddresses(response: IBusinessUnitModel) {
        if (response && response.Addresses && response.Addresses.length > 0) {
            response.Addresses.forEach((address) => this.Addresses.push(new AddressModel(address)));
        }
    }

    isLocked() {
        return !this.IsActivated || this.IsExpired;
    }
}
